import React, { useState, useEffect } from 'react';
import {
  Webhook, Key, Activity, Play, RefreshCw, Copy, Eye, EyeOff,
  Plus, Edit2, Trash2, CheckCircle, XCircle, Clock, AlertCircle
} from 'lucide-react';
import { useAuth } from '../contexts/AuthContext';
import { useToast } from '../hooks/use-toast';

const WebhooksIntegrations = () => {
  const { user } = useAuth();
  const { toast } = useToast();
  const [activeSubTab, setActiveSubTab] = useState('subscriptions');
  const [subscriptions, setSubscriptions] = useState([]);
  const [apiKeys, setApiKeys] = useState([]);
  const [webhookEvents, setWebhookEvents] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showApiKeyModal, setShowApiKeyModal] = useState(false);
  const [showEventDetails, setShowEventDetails] = useState(null);
  const [newApiKeyData, setNewApiKeyData] = useState(null);
  
  // États pour la sélection multiple
  const [selectedSubscriptions, setSelectedSubscriptions] = useState([]);
  const [selectedApiKeys, setSelectedApiKeys] = useState([]);
  const [selectAllSubscriptions, setSelectAllSubscriptions] = useState(false);
  const [selectAllApiKeys, setSelectAllApiKeys] = useState(false);

  // ============== FILTRES ÉVÉNEMENTS ==============
  const [eventFilters, setEventFilters] = useState({
    eventType: 'all',
    status: 'all',
    dateRange: 'all',
    searchQuery: ''
  });

  // Types d'événements disponibles
  const availableEventTypes = [
    'operation.created',
    'operation.status.changed',
    'operation.quote.accepted',
    'operation.quote.rejected',
    'operation.delivered',
    'operation.completed',
    'client.created',
    'client.updated'
  ];

  // Statuts possibles
  const eventStatuses = [
    { value: 'SUCCESS', label: 'Succès', color: 'bg-green-100 text-green-800' },
    { value: 'FAILED', label: 'Échec', color: 'bg-red-100 text-red-800' },
    { value: 'PENDING', label: 'En attente', color: 'bg-yellow-100 text-yellow-800' }
  ];

  // Périodes de temps
  const dateRanges = [
    { value: 'all', label: 'Toutes les dates' },
    { value: 'today', label: "Aujourd'hui" },
    { value: 'week', label: '7 derniers jours' },
    { value: 'month', label: '30 derniers jours' }
  ];

  useEffect(() => {
    if (activeSubTab === 'subscriptions') {
      fetchSubscriptions();
    } else if (activeSubTab === 'api-keys') {
      fetchApiKeys();
    } else if (activeSubTab === 'events') {
      fetchWebhookEvents();
    }
  }, [activeSubTab]);

  const fetchSubscriptions = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/v2/webhook-subscriptions`,
        {
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setSubscriptions(data);
      }
    } catch (error) {
      console.error('Error fetching subscriptions:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchApiKeys = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/api-keys`,
        {
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setApiKeys(data);
      }
    } catch (error) {
      console.error('Error fetching API keys:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchWebhookEvents = async () => {
    setLoading(true);
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/v2/webhook-events?limit=50`,
        {
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );
      if (response.ok) {
        const data = await response.json();
        setWebhookEvents(data);
      }
    } catch (error) {
      console.error('Error fetching webhook events:', error);
    } finally {
      setLoading(false);
    }
  };

  const createWebhookSubscription = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/v2/webhook-subscriptions`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer cookie-auth'
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        const data = await response.json();
        fetchSubscriptions();
        toast({
          title: 'Succès',
          description: 'Webhook créé avec succès',
        });
        // Show the secret in an alert since it won't be shown again
        alert(`⚠️ IMPORTANT: Sauvegardez ce secret maintenant!\n\nSecret: ${data.secret}\n\nCe secret ne sera plus jamais affiché.`);
        return data;
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la création du webhook');
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const createApiKey = async (formData) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/api-keys`,
        {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer cookie-auth'
          },
          body: JSON.stringify(formData)
        }
      );

      if (response.ok) {
        const data = await response.json();
        setNewApiKeyData(data);
        fetchApiKeys();
        toast({
          title: 'Succès',
          description: 'Clé API créée avec succès',
        });
        return data;
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors de la création de la clé API');
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message,
        variant: 'destructive'
      });
    }
  };

  const revokeApiKey = async (keyId) => {
    if (!confirm('Êtes-vous sûr de vouloir révoquer cette clé API ?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/api-keys/${keyId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );

      if (response.ok) {
        fetchApiKeys();
        toast({
          title: 'Succès',
          description: 'Clé API révoquée',
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la révocation',
        variant: 'destructive'
      });
    }
  };

  const deleteSubscription = async (subscriptionId) => {
    if (!confirm('Êtes-vous sûr de vouloir supprimer ce webhook ?')) return;

    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/v2/webhook-subscriptions/${subscriptionId}`,
        {
          method: 'DELETE',
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );

      if (response.ok) {
        fetchSubscriptions();
        toast({
          title: 'Succès',
          description: 'Webhook supprimé',
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la suppression',
        variant: 'destructive'
      });
    }
  };

  const bulkDeleteSubscriptions = async () => {
    if (selectedSubscriptions.length === 0) {
      toast({
        title: 'Aucune sélection',
        description: 'Veuillez sélectionner au moins un webhook',
        variant: 'destructive'
      });
      return;
    }

    if (!confirm(`Êtes-vous sûr de vouloir supprimer ${selectedSubscriptions.length} webhook(s) ?`)) return;

    try {
      const deletePromises = selectedSubscriptions.map(id =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/v2/webhook-subscriptions/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        })
      );

      await Promise.all(deletePromises);
      
      setSelectedSubscriptions([]);
      setSelectAll(false);
      fetchSubscriptions();
      
      toast({
        title: 'Succès',
        description: `${selectedSubscriptions.length} webhook(s) supprimé(s)`,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la suppression en masse',
        variant: 'destructive'
      });
    }
  };

  const bulkDeleteApiKeys = async () => {
    if (selectedApiKeys.length === 0) {
      toast({
        title: 'Aucune sélection',
        description: 'Veuillez sélectionner au moins une clé API',
        variant: 'destructive'
      });
      return;
    }

    if (!confirm(`Êtes-vous sûr de vouloir révoquer ${selectedApiKeys.length} clé(s) API ?`)) return;

    try {
      const deletePromises = selectedApiKeys.map(id =>
        fetch(`${process.env.REACT_APP_BACKEND_URL}/api/integrations/api-keys/${id}`, {
          method: 'DELETE',
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        })
      );

      await Promise.all(deletePromises);
      
      setSelectedApiKeys([]);
      setSelectAll(false);
      fetchApiKeys();
      
      toast({
        title: 'Succès',
        description: `${selectedApiKeys.length} clé(s) API révoquée(s)`,
      });
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la révocation en masse',
        variant: 'destructive'
      });
    }
  };

  const handleSelectAllSubscriptions = (checked) => {
    setSelectAllSubscriptions(checked);
    if (checked) {
      setSelectedSubscriptions(subscriptions.map(s => s.id));
    } else {
      setSelectedSubscriptions([]);
    }
  };

  const handleSelectAllApiKeys = (checked) => {
    setSelectAllApiKeys(checked);
    if (checked) {
      setSelectedApiKeys(apiKeys.map(k => k.id));
    } else {
      setSelectedApiKeys([]);
    }
  };

  const toggleSubscriptionSelection = (id) => {
    setSelectedSubscriptions(prev => {
      const newSelection = prev.includes(id) 
        ? prev.filter(subId => subId !== id)
        : [...prev, id];
      // Mettre à jour le selectAll en fonction de la sélection
      setSelectAllSubscriptions(newSelection.length === subscriptions.length);
      return newSelection;
    });
  };

  const toggleApiKeySelection = (id) => {
    setSelectedApiKeys(prev => {
      const newSelection = prev.includes(id) 
        ? prev.filter(keyId => keyId !== id)
        : [...prev, id];
      // Mettre à jour le selectAll en fonction de la sélection
      setSelectAllApiKeys(newSelection.length === apiKeys.length);
      return newSelection;
    });
  };

  const toggleSubscription = async (subscriptionId, isActive) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/v2/webhook-subscriptions/${subscriptionId}`,
        {
          method: 'PATCH',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': 'Bearer cookie-auth'
          },
          body: JSON.stringify({ isActive: !isActive })
        }
      );

      if (response.ok) {
        fetchSubscriptions();
        toast({
          title: 'Succès',
          description: `Webhook ${!isActive ? 'activé' : 'désactivé'}`,
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la mise à jour',
        variant: 'destructive'
      });
    }
  };

  const retryWebhook = async (eventId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/v2/webhook-events/${eventId}/retry`,
        {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );

      if (response.ok) {
        fetchWebhookEvents();
        toast({
          title: 'Succès',
          description: 'Webhook relancé',
        });
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: 'Erreur lors de la relance',
        variant: 'destructive'
      });
    }
  };

  const testWebhook = async (subscriptionId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/integrations/v2/webhook-subscriptions/${subscriptionId}/test`,
        {
          method: 'POST',
          headers: {
            'Authorization': 'Bearer cookie-auth'
          }
        }
      );

      if (response.ok) {
        const data = await response.json();
        toast({
          title: 'Test envoyé',
          description: data.message || 'Événement de test envoyé avec succès',
        });
        // Refresh events to show the test event
        setTimeout(() => {
          if (activeSubTab === 'events') {
            fetchWebhookEvents();
          }
        }, 1000);
      } else {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || 'Erreur lors du test');
      }
    } catch (error) {
      toast({
        title: 'Erreur',
        description: error.message || 'Erreur lors du test du webhook',
        variant: 'destructive'
      });
    }
  };

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    toast({
      title: 'Copié',
      description: 'Copié dans le presse-papiers',
    });
  };

  const getStatusBadge = (status) => {
    const styles = {
      SENT: 'bg-green-100 text-green-800',
      FAILED: 'bg-red-100 text-red-800',
      PENDING: 'bg-yellow-100 text-yellow-800',
      RETRYING: 'bg-blue-100 text-blue-800'
    };

    const icons = {
      SENT: CheckCircle,
      FAILED: XCircle,
      PENDING: Clock,
      RETRYING: RefreshCw
    };

    const Icon = icons[status] || AlertCircle;
    const className = styles[status] || 'bg-gray-100 text-gray-800';

    return (
      <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${className}`}>
        <Icon className="w-3 h-3 mr-1" />
        {status}
      </span>
    );
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="bg-gradient-to-r from-purple-600 to-indigo-600 rounded-lg p-6 text-white">
        <div className="flex items-center gap-3">
          <Webhook className="w-8 h-8" />
          <div>
            <h2 className="text-2xl font-bold">Webhooks & Intégrations</h2>
            <p className="text-purple-100 mt-1">
              Automatisez vos workflows avec n8n, Teams, et autres outils
            </p>
          </div>
        </div>
      </div>

      {/* Sub-tabs */}
      <div className="flex gap-2 border-b border-gray-200">
        <button
          onClick={() => setActiveSubTab('subscriptions')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeSubTab === 'subscriptions'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Webhook className="w-4 h-4 inline mr-2" />
          Webhooks
        </button>
        <button
          onClick={() => setActiveSubTab('api-keys')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeSubTab === 'api-keys'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Key className="w-4 h-4 inline mr-2" />
          Clés API
        </button>
        <button
          onClick={() => setActiveSubTab('events')}
          className={`px-4 py-2 font-medium border-b-2 transition-colors ${
            activeSubTab === 'events'
              ? 'border-purple-600 text-purple-600'
              : 'border-transparent text-gray-500 hover:text-gray-700'
          }`}
        >
          <Activity className="w-4 h-4 inline mr-2" />
          Événements
        </button>
      </div>

      {/* Subscriptions Tab */}
      {activeSubTab === 'subscriptions' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold">Subscriptions Webhook</h3>
              {selectedSubscriptions.length > 0 && (
                <button
                  onClick={bulkDeleteSubscriptions}
                  className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Supprimer ({selectedSubscriptions.length})
                </button>
              )}
            </div>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" />
              Nouveau Webhook
            </button>
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : subscriptions.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Webhook className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Aucun webhook configuré</p>
            </div>
          ) : (
            <>
              {subscriptions.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectAllSubscriptions}
                    onChange={(e) => handleSelectAllSubscriptions(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-600">Tout sélectionner</span>
                </div>
              )}
              <div className="space-y-3">
                {subscriptions.map((sub) => (
                  <div
                    key={sub.id}
                    className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedSubscriptions.includes(sub.id)}
                        onChange={() => toggleSubscriptionSelection(sub.id)}
                        className="mt-1 w-4 h-4"
                      />
                      <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{sub.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs ${
                          sub.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {sub.isActive ? 'Actif' : 'Inactif'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div className="flex items-center gap-2">
                          <span className="font-medium">URL:</span>
                          <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                            {sub.targetUrl}
                          </code>
                          <button
                            onClick={() => copyToClipboard(sub.targetUrl)}
                            className="text-gray-400 hover:text-gray-600"
                          >
                            <Copy className="w-3 h-3" />
                          </button>
                        </div>
                        <div>
                          <span className="font-medium">Événements:</span>
                          <div className="flex flex-wrap gap-1 mt-1">
                            {sub.eventTypes.map((event) => (
                              <span
                                key={event}
                                className="bg-purple-100 text-purple-800 px-2 py-0.5 rounded text-xs"
                              >
                                {event}
                              </span>
                            ))}
                          </div>
                        </div>
                      </div>
                      </div>
                      <div className="flex gap-2">
                        <button
                          onClick={() => testWebhook(sub.id)}
                          className="p-2 text-purple-600 hover:text-purple-800"
                          title="Tester le webhook"
                        >
                          <Play className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => toggleSubscription(sub.id, sub.isActive)}
                          className="p-2 text-gray-600 hover:text-gray-800"
                          title={sub.isActive ? 'Désactiver' : 'Activer'}
                        >
                          {sub.isActive ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                        </button>
                        <button
                          onClick={() => deleteSubscription(sub.id)}
                          className="p-2 text-red-600 hover:text-red-800"
                          title="Supprimer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* API Keys Tab */}
      {activeSubTab === 'api-keys' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center gap-3">
              <h3 className="text-lg font-semibold">Clés API</h3>
              {selectedApiKeys.length > 0 && (
                <button
                  onClick={bulkDeleteApiKeys}
                  className="flex items-center gap-2 px-3 py-1.5 bg-red-600 text-white rounded-lg hover:bg-red-700 text-sm"
                >
                  <Trash2 className="w-4 h-4" />
                  Révoquer ({selectedApiKeys.length})
                </button>
              )}
            </div>
            <button
              onClick={() => setShowApiKeyModal(true)}
              className="flex items-center gap-2 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              <Plus className="w-4 h-4" />
              Nouvelle Clé API
            </button>
          </div>

          {newApiKeyData && (
            <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
              <div className="flex items-start gap-3">
                <AlertCircle className="w-5 h-5 text-yellow-600 mt-0.5" />
                <div className="flex-1">
                  <h4 className="font-semibold text-yellow-900 mb-2">
                    ⚠️ Clé API créée - Sauvegardez-la maintenant !
                  </h4>
                  <p className="text-sm text-yellow-800 mb-3">
                    Cette clé ne sera plus jamais affichée. Copiez-la dans un endroit sûr.
                  </p>
                  <div className="bg-white rounded p-3 font-mono text-sm">
                    <div className="flex items-center justify-between">
                      <code className="text-purple-600">{newApiKeyData.key}</code>
                      <button
                        onClick={() => copyToClipboard(newApiKeyData.key)}
                        className="ml-3 p-2 bg-purple-100 text-purple-600 rounded hover:bg-purple-200"
                      >
                        <Copy className="w-4 h-4" />
                      </button>
                    </div>
                  </div>
                  <button
                    onClick={() => setNewApiKeyData(null)}
                    className="mt-3 text-sm text-yellow-700 hover:text-yellow-900 underline"
                  >
                    J'ai sauvegardé la clé
                  </button>
                </div>
              </div>
            </div>
          )}

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : apiKeys.length === 0 ? (
            <div className="text-center py-12 bg-gray-50 rounded-lg">
              <Key className="w-12 h-12 text-gray-400 mx-auto mb-3" />
              <p className="text-gray-500">Aucune clé API créée</p>
            </div>
          ) : (
            <>
              {apiKeys.length > 0 && (
                <div className="flex items-center gap-2 px-4 py-2 bg-gray-50 rounded-lg border border-gray-200">
                  <input
                    type="checkbox"
                    checked={selectAllApiKeys}
                    onChange={(e) => handleSelectAllApiKeys(e.target.checked)}
                    className="w-4 h-4"
                  />
                  <span className="text-sm text-gray-600">Tout sélectionner</span>
                </div>
              )}
              <div className="space-y-3">
                {apiKeys.map((key) => (
                  <div
                    key={key.id}
                    className="bg-white border border-gray-200 rounded-lg p-4"
                  >
                    <div className="flex items-start gap-3">
                      <input
                        type="checkbox"
                        checked={selectedApiKeys.includes(key.id)}
                        onChange={() => toggleApiKeySelection(key.id)}
                        className="mt-1 w-4 h-4"
                      />
                      <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <h4 className="font-semibold text-gray-900">{key.name}</h4>
                        <span className={`px-2 py-1 rounded text-xs ${
                          key.isActive 
                            ? 'bg-green-100 text-green-800' 
                            : 'bg-gray-100 text-gray-800'
                        }`}>
                          {key.isActive ? 'Active' : 'Révoquée'}
                        </span>
                      </div>
                      <div className="text-sm text-gray-600 space-y-1">
                        <div>
                          <span className="font-medium">Préfixe:</span>{' '}
                          <code className="bg-gray-100 px-2 py-0.5 rounded text-xs">
                            {key.keyPrefix}
                          </code>
                        </div>
                        <div>
                          <span className="font-medium">Permissions:</span>{' '}
                          {key.permissions.join(', ')}
                        </div>
                        <div className="text-xs text-gray-500">
                          Créée le {new Date(key.createdAt).toLocaleDateString('fr-FR')}
                          {key.lastUsedAt && (
                            <> • Dernière utilisation: {new Date(key.lastUsedAt).toLocaleDateString('fr-FR')}</>
                          )}
                        </div>
                      </div>
                      </div>
                      {key.isActive && (
                        <button
                          onClick={() => revokeApiKey(key.id)}
                          className="p-2 text-red-600 hover:text-red-800"
                          title="Révoquer"
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      )}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}
        </div>
      )}

      {/* Events Tab */}
      {activeSubTab === 'events' && (
        <div className="space-y-4">
          <div className="flex justify-between items-center">
            <h3 className="text-lg font-semibold">Événements Webhook</h3>
            <button
              onClick={fetchWebhookEvents}
              className="flex items-center gap-2 px-4 py-2 bg-gray-100 text-gray-700 rounded-lg hover:bg-gray-200"
            >
              <RefreshCw className="w-4 h-4" />
              Actualiser
            </button>
          </div>

          {/* ============== FILTRES ÉVÉNEMENTS ============== */}
          <div className="bg-gray-50 border border-gray-200 rounded-lg p-4 space-y-3">
            <div className="flex items-center gap-2 text-sm font-medium text-gray-700">
              <Activity className="w-4 h-4" />
              Filtres
            </div>
            <div className="grid grid-cols-1 md:grid-cols-4 gap-3">
              {/* Filtre par type d'événement */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Type d'événement</label>
                <select
                  value={eventFilters.eventType}
                  onChange={(e) => setEventFilters(prev => ({ ...prev, eventType: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">Tous les types</option>
                  {availableEventTypes.map((type) => (
                    <option key={type} value={type}>{type}</option>
                  ))}
                </select>
              </div>

              {/* Filtre par statut */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Statut</label>
                <select
                  value={eventFilters.status}
                  onChange={(e) => setEventFilters(prev => ({ ...prev, status: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  <option value="all">Tous les statuts</option>
                  {eventStatuses.map((status) => (
                    <option key={status.value} value={status.value}>{status.label}</option>
                  ))}
                </select>
              </div>

              {/* Filtre par période */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Période</label>
                <select
                  value={eventFilters.dateRange}
                  onChange={(e) => setEventFilters(prev => ({ ...prev, dateRange: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                >
                  {dateRanges.map((range) => (
                    <option key={range.value} value={range.value}>{range.label}</option>
                  ))}
                </select>
              </div>

              {/* Recherche par nom de subscription */}
              <div>
                <label className="text-xs text-gray-500 mb-1 block">Recherche</label>
                <input
                  type="text"
                  placeholder="Nom du webhook..."
                  value={eventFilters.searchQuery}
                  onChange={(e) => setEventFilters(prev => ({ ...prev, searchQuery: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-purple-500 focus:border-transparent"
                />
              </div>
            </div>

            {/* Bouton réinitialiser les filtres */}
            {(eventFilters.eventType !== 'all' || eventFilters.status !== 'all' || eventFilters.dateRange !== 'all' || eventFilters.searchQuery) && (
              <div className="flex justify-end">
                <button
                  onClick={() => setEventFilters({ eventType: 'all', status: 'all', dateRange: 'all', searchQuery: '' })}
                  className="text-xs text-purple-600 hover:text-purple-800 underline"
                >
                  Réinitialiser les filtres
                </button>
              </div>
            )}
          </div>

          {loading ? (
            <div className="text-center py-8">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-purple-600 mx-auto"></div>
            </div>
          ) : (() => {
            // Appliquer les filtres côté client
            let filteredEvents = [...webhookEvents];

            // Filtre par type d'événement
            if (eventFilters.eventType !== 'all') {
              filteredEvents = filteredEvents.filter(e => e.eventType === eventFilters.eventType);
            }

            // Filtre par statut
            if (eventFilters.status !== 'all') {
              filteredEvents = filteredEvents.filter(e => e.status === eventFilters.status);
            }

            // Filtre par période
            if (eventFilters.dateRange !== 'all') {
              const now = new Date();
              let minDate;
              
              switch (eventFilters.dateRange) {
                case 'today':
                  minDate = new Date(now.getFullYear(), now.getMonth(), now.getDate());
                  break;
                case 'week':
                  minDate = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
                  break;
                case 'month':
                  minDate = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
                  break;
                default:
                  minDate = null;
              }

              if (minDate) {
                filteredEvents = filteredEvents.filter(e => new Date(e.createdAt) >= minDate);
              }
            }

            // Filtre par recherche
            if (eventFilters.searchQuery.trim()) {
              const query = eventFilters.searchQuery.toLowerCase();
              filteredEvents = filteredEvents.filter(e => 
                e.subscriptionName?.toLowerCase().includes(query) ||
                e.eventType?.toLowerCase().includes(query)
              );
            }

            // Affichage des résultats filtrés
            if (filteredEvents.length === 0) {
              return (
                <div className="text-center py-12 bg-gray-50 rounded-lg">
                  <Activity className="w-12 h-12 text-gray-400 mx-auto mb-3" />
                  <p className="text-gray-500">
                    {webhookEvents.length === 0 
                      ? 'Aucun événement enregistré' 
                      : 'Aucun événement ne correspond aux filtres'}
                  </p>
                </div>
              );
            }

            return (
              <>
                {/* Compteur de résultats */}
                <div className="text-sm text-gray-500">
                  {filteredEvents.length} événement{filteredEvents.length > 1 ? 's' : ''} 
                  {webhookEvents.length !== filteredEvents.length && ` (sur ${webhookEvents.length} total)`}
                </div>

                <div className="space-y-2">
                  {filteredEvents.map((event) => (
                    <div
                      key={event.id}
                      className="bg-white border border-gray-200 rounded-lg p-4 hover:shadow-sm transition-shadow"
                    >
                      <div className="flex items-start justify-between">
                        <div className="flex-1">
                          <div className="flex items-center gap-3 mb-2">
                            <span className="font-mono text-sm text-purple-600">{event.eventType}</span>
                            {getStatusBadge(event.status)}
                            <span className="text-xs text-gray-500">
                              {new Date(event.createdAt).toLocaleString('fr-FR')}
                            </span>
                          </div>
                          <div className="text-sm text-gray-600">
                            <div>→ {event.subscriptionName}</div>
                            {event.status === 'FAILED' && event.errorMessage && (
                              <div className="text-red-600 text-xs mt-1">
                                Erreur: {event.errorMessage}
                              </div>
                            )}
                            {event.responseStatusCode && (
                              <div className="text-xs text-gray-500 mt-1">
                                Status HTTP: {event.responseStatusCode} • Tentatives: {event.attemptCount}
                              </div>
                            )}
                          </div>
                        </div>
                        <div className="flex gap-2">
                          <button
                            onClick={() => setShowEventDetails(event)}
                            className="p-2 text-gray-600 hover:text-gray-800"
                            title="Voir détails"
                          >
                            <Eye className="w-4 h-4" />
                          </button>
                          {event.status === 'FAILED' && (
                            <button
                              onClick={() => retryWebhook(event.id)}
                              className="p-2 text-blue-600 hover:text-blue-800"
                              title="Relancer"
                            >
                              <RefreshCw className="w-4 h-4" />
                            </button>
                          )}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </>
            );
          })()}
        </div>
      )}

      {/* Create Webhook Modal */}
      {showCreateModal && (
        <WebhookCreateModal
          onClose={() => setShowCreateModal(false)}
          onCreate={createWebhookSubscription}
        />
      )}

      {/* Create API Key Modal */}
      {showApiKeyModal && (
        <ApiKeyCreateModal
          onClose={() => setShowApiKeyModal(false)}
          onCreate={createApiKey}
        />
      )}

      {/* Event Details Modal */}
      {showEventDetails && (
        <EventDetailsModal
          event={showEventDetails}
          onClose={() => setShowEventDetails(null)}
        />
      )}
    </div>
  );
};

// Webhook Create Modal Component
const WebhookCreateModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    targetUrl: '',
    eventTypes: ['operation.created'],
    scope: 'INTERNAL'
  });

  const availableEvents = [
    'operation.created',
    'operation.status.changed',
    'operation.quote.accepted',
    'operation.quote.rejected',
    'operation.delivered',
    'operation.completed',
    'client.created',
    'client.updated'
  ];

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onCreate(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        <h3 className="text-lg font-semibold mb-4">Créer un Webhook</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom *
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="Microsoft Teams - Nouvelles Opérations"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              URL de destination *
            </label>
            <input
              type="url"
              value={formData.targetUrl}
              onChange={(e) => setFormData({ ...formData, targetUrl: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg font-mono text-sm"
              placeholder="https://your-n8n.com/webhook/..."
              required
            />
            <p className="text-xs text-gray-500 mt-1">
              URL du webhook n8n, Teams, ou autre service
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Types d'événements *
            </label>
            <div className="space-y-2 max-h-48 overflow-y-auto border border-gray-200 rounded-lg p-3">
              {availableEvents.map(event => (
                <label key={event} className="flex items-center hover:bg-gray-50 p-1 rounded">
                  <input
                    type="checkbox"
                    checked={formData.eventTypes.includes(event)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, eventTypes: [...formData.eventTypes, event] });
                      } else {
                        setFormData({ ...formData, eventTypes: formData.eventTypes.filter(ev => ev !== event) });
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm font-mono text-purple-600">{event}</span>
                </label>
              ))}
            </div>
            {formData.eventTypes.length === 0 && (
              <p className="text-xs text-red-600 mt-1">Sélectionnez au moins un événement</p>
            )}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Scope
            </label>
            <select
              value={formData.scope}
              onChange={(e) => setFormData({ ...formData, scope: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
            >
              <option value="INTERNAL">Internal (équipe interne)</option>
              <option value="CLIENT">Client (notifications clients)</option>
              <option value="GLOBAL">Global (tous)</option>
            </select>
          </div>

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-3">
            <p className="text-sm text-blue-800">
              ℹ️ Un secret de signature HMAC sera généré automatiquement pour sécuriser ce webhook.
            </p>
          </div>

          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              disabled={formData.eventTypes.length === 0}
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// API Key Create Modal Component
const ApiKeyCreateModal = ({ onClose, onCreate }) => {
  const [formData, setFormData] = useState({
    name: '',
    permissions: ['read:operations']
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    await onCreate(formData);
    onClose();
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg p-6 max-w-md w-full">
        <h3 className="text-lg font-semibold mb-4">Créer une Clé API</h3>
        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Nom
            </label>
            <input
              type="text"
              value={formData.name}
              onChange={(e) => setFormData({ ...formData, name: e.target.value })}
              className="w-full px-3 py-2 border border-gray-300 rounded-lg"
              placeholder="n8n Integration Key"
              required
            />
          </div>
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Permissions
            </label>
            <div className="space-y-2">
              {['read:operations', 'read:clients', 'read:drivers', 'read:vehicles'].map(perm => (
                <label key={perm} className="flex items-center">
                  <input
                    type="checkbox"
                    checked={formData.permissions.includes(perm)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setFormData({ ...formData, permissions: [...formData.permissions, perm] });
                      } else {
                        setFormData({ ...formData, permissions: formData.permissions.filter(p => p !== perm) });
                      }
                    }}
                    className="mr-2"
                  />
                  <span className="text-sm">{perm}</span>
                </label>
              ))}
            </div>
          </div>
          <div className="flex gap-3 pt-4">
            <button
              type="button"
              onClick={onClose}
              className="flex-1 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              Annuler
            </button>
            <button
              type="submit"
              className="flex-1 px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700"
            >
              Créer
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

// Event Details Modal Component
const EventDetailsModal = ({ event, onClose }) => {
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
      <div className="bg-white rounded-lg p-6 max-w-3xl w-full max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold">Détails de l'Événement</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            ✕
          </button>
        </div>
        <div className="space-y-4">
          <div>
            <label className="text-sm font-medium text-gray-700">Event ID</label>
            <code className="block bg-gray-100 p-2 rounded text-xs mt-1">{event.eventId}</code>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Type</label>
            <p className="text-sm mt-1">{event.eventType}</p>
          </div>
          <div>
            <label className="text-sm font-medium text-gray-700">Payload</label>
            <pre className="bg-gray-900 text-green-400 p-3 rounded text-xs mt-1 overflow-x-auto">
              {JSON.stringify(event.payload || event.data || {}, null, 2)}
            </pre>
          </div>
          {event.responseBody && (
            <div>
              <label className="text-sm font-medium text-gray-700">Réponse</label>
              <pre className="bg-gray-100 p-3 rounded text-xs mt-1 overflow-x-auto">
                {event.responseBody}
              </pre>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default WebhooksIntegrations;
